// `useValidateProvisioningToken` — wraps GET /api/public/auth/provisioning/validate.
//
// Public endpoint, no Bearer required: the apiClient's auth-token
// provider returns null when the user is unauthenticated and the
// `Authorization` header is omitted entirely. So the same `apiClient`
// used by the operational surface works for this anonymous flow
// without a parallel publicApiClient.
//
// Loading / fail discrimination:
//   The page branches on `query.error` (instance of ApiError). The
//   apiClient parses the BE fail envelope into an ApiError with the
//   HTTP status preserved on `.status`, so the page checks `status`
//   to discriminate 404 (unknown) vs 410 (consumed/expired) vs other.
//
// Caching:
//   Each token gets its own cache entry via `qk.provisioning.validate(token)`.
//   `staleTime` matches the project default (30s); `retry: false` so a
//   404 doesn't trigger a noisy second hit (the apiClient retry policy
//   already skips 4xx — explicit here for clarity).

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { qk } from '@/lib/queryKeys';
import type { ProvisioningValidateResponse } from '../types';

export function useValidateProvisioningToken(
  token: string | null,
): UseQueryResult<ProvisioningValidateResponse> {
  return useQuery<ProvisioningValidateResponse>({
    queryKey: qk.provisioning.validate(token ?? ''),
    queryFn: () =>
      apiClient.get<ProvisioningValidateResponse>(
        '/api/public/auth/provisioning/validate',
        { query: { token: token ?? '' } },
      ),
    enabled: !!token && token.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}
